import "./style.scss";

import { Avatar } from "@mui/material";
import PickerCompany from "components/BPPickerCompany";
import FilterInput from "components/shared/FilterInput";
import { useIsMobile } from "hooks/useIsMobile";
import { useIsPWA } from "hooks/usePWAMode";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCompagnies } from "store/beneficiaryStore";
import { toggleTitleDrawer } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchTitlesByBeneficiar } from "store/titleStore";
import type { IBeneficiariesCompanies, TParamsPagination } from "store/types";

const TitlesList = (props: any) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { beneficiarTitles, beneficiarTitlesStatus, countBeneficiarTitles } = useAppSelector((state) => state.titles);
    const { beneficiarCompagnies } = useAppSelector((state) => state.beneficiary);

    const [query, setQuery] = useState<string>("");
    const [queryIsTyping, setQueryIsTyping] = useState<boolean | null>(null);
    const [positionScroll, setPositionScroll] = useState<number>(0);
    const isMounted = useRef(false);
    const [timeOut, setTimeOut] = useState<NodeJS.Timeout | undefined>();
    const [chosenCompagnies, setChosenCompagnies] = useState<IBeneficiariesCompanies[]>([]);
    // Different companies portal type
    const portalTypes = chosenCompagnies.length
        ? chosenCompagnies.map((c) => c?.publisherType).join("_")
        : beneficiarCompagnies.map((c) => c?.publisherType ?? "").join("_");

    const { isMobile } = useIsMobile();
    const isPWAMode = useIsPWA();

    const handleRequest = (offset?: number) => {
        const options: TParamsPagination = { limit: 100 };
        offset && (options.offset = offset);
        query && (options.query = query);
        options.companyIds = chosenCompagnies.map((comp) => comp.id);
        dispatch(fetchTitlesByBeneficiar(options));
    };

    const handleQuery = (e: any) => {
        setQuery(e);
        // Debounce call endpoint when search
        if (!queryIsTyping) {
            setTimeOut(
                setTimeout(() => {
                    setQueryIsTyping(false);
                }, 700),
            );
            setQueryIsTyping(true);
        } else {
            clearTimeout(timeOut);
            setTimeOut(
                setTimeout(() => {
                    setQueryIsTyping(false);
                }, 700),
            );
        }
    };

    const handleScroll = (ev: any) => {
        const target = ev.target as HTMLElement;
        setPositionScroll(
            (target.offsetHeight * 100) / target.scrollHeight + (target.scrollTop * 100) / target.scrollHeight,
        );
    };

    useEffect(() => {
        if (queryIsTyping === false) {
            handleRequest();
        }
    }, [queryIsTyping]);

    useEffect(() => {
        if (
            beneficiarTitlesStatus !== "pending" &&
            positionScroll > 70 &&
            beneficiarTitles.length < countBeneficiarTitles
        ) {
            handleRequest(beneficiarTitles.length);
        }
    }, [positionScroll]);

    useEffect(() => {
        if (isMounted.current) {
            handleRequest();
        }
    }, [chosenCompagnies]);

    useEffect(() => {
        dispatch(getCompagnies()).then((resp) => {
            if (resp.meta.requestStatus === "fulfilled") {
                setChosenCompagnies(resp.payload.data.companies);
            }
        });
    }, []);

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
    }, []);

    const onOpenTitle = (elem: any, props: any) => {
        props.setCurrentTitle(elem);
        if (isMobile) dispatch(toggleTitleDrawer(true));
    };

    const dynamicTitle = useMemo(() => {
        let title = "";
        if (portalTypes.includes("book")) {
            title += t("Titles.Titles");
        }
        if (portalTypes.includes("press")) {
            title += " / " + t("Publications.Publication_press_other");
        }
        if (portalTypes.includes("audiovisual")) {
            title += " / " + t("Publications.Publication_audiovisual_other");
        }
        if (title.startsWith(" / ")) {
            title = title.slice(3);
        }
        return title;
    }, [portalTypes]);

    return (
        <div style={!isMobile ? { minWidth: 335 } : {}}>
            <div className="titles-bp-list-container" style={isPWAMode ? { paddingBottom: "6.5rem" } : {}}>
                <div className="titles-list-head">
                    <div className="titles-list-header">
                        {`${dynamicTitle}
            (${props.titles.length})`}
                    </div>
                    {beneficiarCompagnies.length > 1 && (
                        <div className="titles-list-picker-company">
                            <PickerCompany
                                data={beneficiarCompagnies}
                                selectedData={chosenCompagnies}
                                setSelectedData={setChosenCompagnies}
                            />
                        </div>
                    )}
                    <div className="titles-list-filter">
                        <div className="titles-list-search" style={{ width: "100%" }}>
                            <FilterInput filterValue={query} setFilterValue={handleQuery} />
                        </div>
                    </div>
                </div>
                <div className="titles-list" onScroll={(ev) => handleScroll(ev)}>
                    {props.titles.map((elem: any, index: number) => {
                        const usesISSN = !!elem.otherInformation?.issn;
                        return (
                            <div
                                key={elem.id}
                                className={`titles-list-element${elem.id === props.currentTitle ? " active" : ""}`}
                                onClick={() => onOpenTitle(elem, props)}
                            >
                                <div
                                    className="avatar-block"
                                    style={{
                                        display: "flex",
                                        alignItems: "stretch",
                                        gap: "10px",
                                    }}
                                >
                                    <Avatar
                                        variant="rounded"
                                        src={elem.avatarUrl}
                                        sx={{
                                            backgroundColor: "#FFF2F1",
                                            color: "black",
                                        }}
                                    >
                                        {elem.name.slice(0, 1)}
                                    </Avatar>
                                    <div
                                        className="title-info-wrapper"
                                        style={{ display: "flex", flexDirection: "column" }}
                                    >
                                        <span title={elem.name} className="title-list-title-name">
                                            {elem.name}
                                        </span>
                                        <span className="title-list-title-ean">
                                            {usesISSN ? elem.otherInformation?.issn : elem.ean13}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default TitlesList;
